import { api, handleApiError } from './api';
import { memoize } from '../utils/memoization';
import { createApiCacheKey, cacheApiResponse, getCachedApiResponse } from '../utils/cache';

export interface DashboardKpis {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  cashFlow: number;
  receivables: number;
  payables: number;
  gstLiability: number;
  profitMargin: number;
  revenueGrowth: number;
  expenseGrowth: number;
  daysSinceLastTransaction: number;
  averageDailyRevenue: number;
  averageDailyExpenses: number;
  transactionCount: number;
  incomeTransactionCount: number;
  expenseTransactionCount: number;
}

/** GET /api/dashboard/overview — kpis + filings + org flag only */
export interface DashboardOverviewOnly {
  kpis: DashboardKpis;
  hasOrganization?: boolean;
  pendingGSTFilings?: number;
  overdueFilings?: number;
}

export type RevenueExpenseTrendItem = {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
  gstLiability: number;
  monthIndex: number;
};

/** GET /api/dashboard/monthly?year= */
export interface DashboardMonthly {
  revenueExpenseTrend: RevenueExpenseTrendItem[];
  expenseByCategory: Array<{ name: string; value: number }>;
  hasOrganization?: boolean;
}

/** GET /api/dashboard/recent */
export interface DashboardRecent {
  recentTransactions: Array<{
    id: string;
    date: string;
    description?: string;
    amount: number;
    type: 'income' | 'expense';
    category?: string;
  }>;
  hasOrganization?: boolean;
}

/** Merged view used by Dashboard / Reports (parallel overview + monthly + recent). */
export interface DashboardMergedView extends DashboardOverviewOnly {
  revenueExpenseTrend: RevenueExpenseTrendItem[];
  expenseByCategory: Array<{ name: string; value: number }>;
  recentTransactions: DashboardRecent['recentTransactions'];
}

export interface GSTDashboardData {
  summary: {
    total: number;
    pending: number;
    filed: number;
    overdue: number;
    cancelled: number;
  };
  amounts: {
    totalTaxableValue: number;
    totalGSTAmount: number;
    totalCGST: number;
    totalSGST: number;
    totalIGST: number;
    totalPenalty: number;
    totalInterest: number;
  };
  monthlyTrends: Array<{
    month: string;
    taxableValue: number;
    gstAmount: number;
    filings: number;
  }>;
  recentFilings: Array<{
    id: string;
    filing_type: string;
    filing_period: string;
    status: string;
    total_taxable_value: number;
    total_gst_amount: number;
    due_date: Date;
    filing_date?: Date;
  }>;
  complianceRate: number;
}

const getCurrentYear = memoize(() => new Date().getFullYear(), { maxSize: 1, ttl: 60 * 1000 });

function mapKpis(data: any): DashboardKpis {
  return {
    totalRevenue: data.kpis.totalRevenue,
    totalExpenses: data.kpis.totalExpenses,
    netProfit: data.kpis.netProfit,
    cashFlow: data.kpis.cashFlow,
    receivables: data.kpis.receivables,
    payables: data.kpis.payables,
    gstLiability: data.kpis.gstLiability,
    profitMargin: data.kpis.profitMargin || 0,
    revenueGrowth: data.kpis.revenueGrowth || 0,
    expenseGrowth: data.kpis.expenseGrowth || 0,
    daysSinceLastTransaction: data.kpis.daysSinceLastTransaction || 0,
    averageDailyRevenue: data.kpis.averageDailyRevenue || 0,
    averageDailyExpenses: data.kpis.averageDailyExpenses || 0,
    transactionCount: data.kpis.transactionCount || 0,
    incomeTransactionCount: data.kpis.incomeTransactionCount || 0,
    expenseTransactionCount: data.kpis.expenseTransactionCount || 0,
  };
}

export function mergeDashboardParts(
  overview: DashboardOverviewOnly,
  monthly: DashboardMonthly | null | undefined,
  recent: DashboardRecent | null | undefined
): DashboardMergedView {
  const trend = (monthly?.revenueExpenseTrend || []).map((item: any, index: number) => ({
    ...item,
    monthIndex: item.monthIndex ?? index,
  }));
  return {
    kpis: overview.kpis,
    hasOrganization:
      overview.hasOrganization !== false &&
      monthly?.hasOrganization !== false &&
      recent?.hasOrganization !== false,
    ...(overview.pendingGSTFilings !== undefined ? { pendingGSTFilings: overview.pendingGSTFilings } : {}),
    ...(overview.overdueFilings !== undefined ? { overdueFilings: overview.overdueFilings } : {}),
    revenueExpenseTrend: trend,
    expenseByCategory: monthly?.expenseByCategory || [],
    recentTransactions: recent?.recentTransactions || [],
  };
}

export const dashboardService = {
  async getOverview(year?: number, organizationId?: string | null): Promise<DashboardOverviewOnly> {
    const cacheKey = createApiCacheKey('/dashboard/overview', {
      year: year || getCurrentYear(),
      organizationId: organizationId || 'none',
    });
    const cached = getCachedApiResponse<DashboardOverviewOnly>(cacheKey);
    if (cached) return cached;

    try {
      const response = await api.get(`/dashboard/overview?year=${year || getCurrentYear()}`);
      const data = response.data.data;

      const result: DashboardOverviewOnly = {
        kpis: mapKpis(data),
        hasOrganization: data.hasOrganization !== false,
        pendingGSTFilings: data.pendingGSTFilings ?? 0,
        overdueFilings: data.overdueFilings ?? 0,
      };

      cacheApiResponse(cacheKey, result, 5 * 60 * 1000);
      return result;
    } catch (error) {
      return handleApiError(error, 'DashboardService.getOverview');
    }
  },

  async getMonthly(year?: number, organizationId?: string | null): Promise<DashboardMonthly> {
    const cacheKey = createApiCacheKey('/dashboard/monthly', {
      year: year || getCurrentYear(),
      organizationId: organizationId || 'none',
    });
    const cached = getCachedApiResponse<DashboardMonthly>(cacheKey);
    if (cached) return cached;

    try {
      const response = await api.get(`/dashboard/monthly?year=${year || getCurrentYear()}`);
      const data = response.data.data;
      const result: DashboardMonthly = {
        revenueExpenseTrend: (data.revenueExpenseTrend || []).map((item: any, index: number) => ({
          ...item,
          monthIndex: item.monthIndex ?? index,
        })),
        expenseByCategory: data.expenseByCategory || [],
        hasOrganization: data.hasOrganization !== false,
      };
      cacheApiResponse(cacheKey, result, 5 * 60 * 1000);
      return result;
    } catch (error) {
      return handleApiError(error, 'DashboardService.getMonthly');
    }
  },

  async getRecent(organizationId?: string | null): Promise<DashboardRecent> {
    const cacheKey = createApiCacheKey('/dashboard/recent', {
      organizationId: organizationId || 'none',
    });
    const cached = getCachedApiResponse<DashboardRecent>(cacheKey);
    if (cached) return cached;

    try {
      const response = await api.get('/dashboard/recent');
      const data = response.data.data;
      const result: DashboardRecent = {
        recentTransactions: data.recentTransactions || [],
        hasOrganization: data.hasOrganization !== false,
      };
      cacheApiResponse(cacheKey, result, 5 * 60 * 1000);
      return result;
    } catch (error) {
      return handleApiError(error, 'DashboardService.getRecent');
    }
  },

  /** Convenience: parallel fetch for screens that need the full merged snapshot. */
  async getMergedDashboard(year?: number, organizationId?: string | null): Promise<DashboardMergedView> {
    const y = year || getCurrentYear();
    const [overview, monthly, recent] = await Promise.all([
      this.getOverview(y, organizationId),
      this.getMonthly(y, organizationId),
      this.getRecent(organizationId),
    ]);
    return mergeDashboardParts(overview, monthly, recent);
  },

  async getGSTData(year?: number, organizationId?: string | null): Promise<GSTDashboardData> {
    const cacheKey = createApiCacheKey('/dashboard/gst', {
      year: year || getCurrentYear(),
      organizationId: organizationId || 'none',
    });

    const cached = getCachedApiResponse<GSTDashboardData>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await api.get(`/dashboard/gst?year=${year || getCurrentYear()}`);
      const data = response.data.data;

      const result = {
        summary: data.summary,
        amounts: data.amounts,
        monthlyTrends: data.monthlyTrends,
        recentFilings: data.recentFilings.map((filing: any) => ({
          ...filing,
          due_date: new Date(filing.due_date),
          filing_date: filing.filing_date ? new Date(filing.filing_date) : undefined,
        })),
        complianceRate: data.complianceRate,
      };

      cacheApiResponse(cacheKey, result, 5 * 60 * 1000);

      return result;
    } catch (error: any) {
      if (
        error?.response?.status === 400 &&
        (error?.response?.data?.message?.toLowerCase().includes('no organization') ||
          error?.response?.data?.error?.code === 'NO_ORGANIZATION')
      ) {
        return {
          summary: { total: 0, pending: 0, filed: 0, overdue: 0, cancelled: 0 },
          amounts: {
            totalTaxableValue: 0,
            totalGSTAmount: 0,
            totalCGST: 0,
            totalSGST: 0,
            totalIGST: 0,
            totalPenalty: 0,
            totalInterest: 0,
          },
          monthlyTrends: [],
          recentFilings: [],
          complianceRate: 0,
        };
      }
      return handleApiError(error, 'DashboardService.getGSTData');
    }
  },
};
